import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    Drawer, Box, Typography, Stack, TextField, Button,
    Divider, IconButton, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ApiClient from '../../../common/API';
import { useQueryClient } from 'react-query';
import { usePermissionList, useUserList } from '../../../common/API/hooks';
import { useSave } from '../utils';
import { getSelectedOption } from '../../../common/utils';
import DropdownCheckboxV3 from '../../../common/components/DropdownCheckboxV3';
import ConfirmationModal from '../../../common/components/ConfirmationModal';
import handleError from '../ErrorHandling';

const BRAND = '#0F2FEB';
const EMPTY_GROUP = { name: '', permissions: [], users: [] };

export default function GroupDrawer({ isOpen, mode, group, onClose }) {
    const queryClient = useQueryClient();
    const { data: permissionList, isLoading: permissionLoading } = usePermissionList();
    const { data: userList, isLoading: userLoading } = useUserList();
    const { saveData: saveGroups } = useSave();

    const [form, setForm] = useState(EMPTY_GROUP);
    const [hasChanges, setHasChanges] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [saving, setSaving] = useState(false);

    const isEdit = mode === 'edit';

    // Sync form state when the drawer opens or a different group is selected
    useEffect(() => {
        if (isOpen) {
            if (isEdit && group) {
                setForm({
                    name: group.name || '',
                    permissions: group.permissions || [],
                    users: group.users || [],
                });
            } else {
                setForm(EMPTY_GROUP);
            }
            setHasChanges(false);
        }
    }, [isOpen, group, isEdit]);

    const permissionOptions = React.useMemo(() => {
        if (permissionLoading || !permissionList) return [];
        return permissionList.map(p => ({ value: p.name, label: p.name, key: p.id }));
    }, [permissionList, permissionLoading]);

    const userOptions = React.useMemo(() => {
        if (userLoading || !userList) return [];
        return userList.map(u => ({
            value: u.username,
            label: `${u.first_name} ${u.last_name} (${u.username})`,
            key: u.id,
        }));
    }, [userList, userLoading]);

    const handleNameChange = (e) => {
        setHasChanges(true);
        setForm(prev => ({ ...prev, name: e.target.value }));
    };

    const handlePermissionChange = (_, newValue) => {
        setHasChanges(true);
        setForm(prev => ({ ...prev, permissions: newValue || [] }));
    };

    const handleUserChange = (_, newValue) => {
        setHasChanges(true);
        setForm(prev => ({ ...prev, users: newValue || [] }));
    };

    // Show short "username" chips instead of the full "First Last (username)" label
    const userTagsRenderer = React.useCallback((value, getTagProps) => {
        if (!value?.length) return [];
        if (value.length > 0 && value.length === userOptions.length) {
            return (
                <Chip
                    size="small" label="All Users"
                    onDelete={() => handleUserChange(null, [])}
                    sx={{ fontSize: '0.7rem' }}
                />
            );
        }
        return value.map((tag, index) => (
            <Chip
                {...getTagProps({ index })}
                key={index}
                size="small"
                label={tag?.value}
                sx={{ fontSize: '0.7rem' }}
            />
        ));
    }, [userOptions]);

    const handleClose = () => {
        if (hasChanges) setShowWarning(true);
        else onClose();
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = {
            name: form.name,
            permissions: form.permissions,
            users: form.users,
        };
        try {
            if (isEdit) {
                await ApiClient().patch(`group/${group.id}/`, payload);
                queryClient.invalidateQueries(['group']);
                queryClient.invalidateQueries(['user']);
                toast.success('Group updated successfully!');
            } else {
                await saveGroups('group', payload);
                toast.success('Group created successfully!');
            }
            onClose();
        } catch (error) {
            toast.error(handleError(error, 'Group', isEdit ? 'edit' : 'save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={handleClose}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' },
                }}
            >
                {/* Header */}
                <Box sx={{ px: 3, py: 2.5, bgcolor: BRAND, color: 'white', flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            {isEdit
                                ? <EditNoteRoundedIcon sx={{ fontSize: '1.6rem' }} />
                                : <GroupAddRoundedIcon sx={{ fontSize: '1.6rem' }} />}
                            <Typography variant="h6" fontWeight="bold">
                                {isEdit ? 'Edit Group' : 'Create New Group'}
                            </Typography>
                        </Stack>
                        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    {isEdit && group && (
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, ml: 4 }}>
                            {group.name} &bull; {group.users?.length || 0} member{group.users?.length !== 1 ? 's' : ''}
                        </Typography>
                    )}
                </Box>

                <Divider />

                {/* Scrollable Form Body */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                    <Stack spacing={3}>
                        <TextField
                            label="Group Name"
                            size="small"
                            fullWidth
                            required
                            autoFocus
                            value={form.name}
                            onChange={handleNameChange}
                        />

                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                Permissions
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Define what this group is allowed to access.
                            </Typography>
                            <DropdownCheckboxV3
                                options={permissionOptions}
                                label="Select Permission(s)"
                                placeholder="Select Permission(s)"
                                limitTags={2}
                                loading={permissionLoading}
                                onChange={handlePermissionChange}
                                value={getSelectedOption(form.permissions, permissionOptions) || []}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                Members
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Select users to include in this group.
                            </Typography>
                            <DropdownCheckboxV3
                                options={userOptions}
                                label="Select User(s)"
                                placeholder="Select User(s)"
                                limitTags={3}
                                loading={userLoading}
                                onChange={handleUserChange}
                                value={getSelectedOption(form.users, userOptions) || []}
                                tagsRenderer={userTagsRenderer}
                            />
                        </Box>
                    </Stack>
                </Box>

                <Divider />

                {/* Sticky Footer */}
                <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{ textTransform: 'none', color: BRAND, borderColor: BRAND, minWidth: 90 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!form.name || saving}
                            sx={{
                                textTransform: 'none', bgcolor: BRAND, minWidth: 120, fontWeight: 600,
                                '&:hover': { bgcolor: '#0A22C0' },
                            }}
                        >
                            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Group'}
                        </Button>
                    </Stack>
                </Box>
            </Drawer>

            {showWarning && (
                <ConfirmationModal
                    isOpen={showWarning}
                    onClose={() => setShowWarning(false)}
                    onConfirm={() => { setShowWarning(false); onClose(); }}
                    icon={<WarningAmberIcon sx={{ fontSize: '60px' }} color="warning" />}
                    title="Discard Changes?"
                    message="You have unsaved changes. Are you sure you want to close without saving?"
                />
            )}
        </>
    );
}

